import React, { useState, useEffect } from 'react';
import { FiFileText, FiDownload, FiPlus, FiSearch, FiFilter, FiEye, FiMail, FiCalendar, FiDollarSign } from 'react-icons/fi';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [invoiceSettings, setInvoiceSettings] = useState({
    prefix: 'JAS',
    next_number: 1,
    company_info: {}
  });

  useEffect(() => {
    fetchInvoices();
    fetchInvoiceSettings();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await api.get('/invoices');
      setInvoices(response.data.invoices || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Erreur lors du chargement des factures');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoiceSettings = async () => {
    try {
      const response = await api.get('/invoices/settings');
      if (response.data) {
        setInvoiceSettings(response.data);
      }
    } catch (error) {
      console.error('Error fetching invoice settings:', error);
    }
  };

  const handleDownloadPDF = async (invoiceId) => {
    try {
      const response = await api.get(`/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture-${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Facture téléchargée');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleSendEmail = async (invoiceId) => {
    try {
      await api.post(`/invoices/${invoiceId}/send`);
      toast.success('Facture envoyée par email');
    } catch (error) {
      console.error('Error sending invoice:', error);
      toast.error('Erreur lors de l\'envoi');
    }
  };

  const getInvoiceTypeLabel = (type) => {
    switch (type) {
      case 'deposit': return 'Acompte';
      case 'balance': return 'Solde';
      case 'full': return 'Totale';
      default: return type;
    }
  };

  const getInvoiceTypeBadge = (type) => {
    switch (type) {
      case 'deposit':
        return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Acompte</Badge>;
      case 'balance':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Solde</Badge>;
      case 'full':
        return <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">Totale</Badge>;
      default:
        return <Badge>{type}</Badge>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Payée</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">En attente</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Annulée</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = 
      invoice.invoice_number?.toLowerCase().includes(search.toLowerCase()) ||
      invoice.client_name?.toLowerCase().includes(search.toLowerCase()) ||
      invoice.client_email?.toLowerCase().includes(search.toLowerCase());
    
    const matchesType = filterType === 'all' || invoice.type === filterType;
    const matchesStatus = filterStatus === 'all' || invoice.status === filterStatus;
    
    return matchesSearch && matchesType && matchesStatus;
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  // Stats calculations
  const stats = {
    total: invoices.length,
    totalAmount: invoices.reduce((sum, inv) => sum + (inv.amount || 0), 0),
    paid: invoices.filter(inv => inv.status === 'paid').length,
    paidAmount: invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + (inv.amount || 0), 0),
    pending: invoices.filter(inv => inv.status === 'pending').length,
    pendingAmount: invoices.filter(inv => inv.status === 'pending').reduce((sum, inv) => sum + (inv.amount || 0), 0),
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Factures</h1>
          <p className="text-gray-500 text-sm mt-1">
            Gestion des factures d'acompte et de solde
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-5 text-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/10 rounded-lg">
              <FiFileText className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-white/60">Total factures</p>
              <p className="text-xl font-bold">{stats.total}</p>
              <p className="text-sm text-white/80">{formatPrice(stats.totalAmount)}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-xl p-5 text-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/10 rounded-lg">
              <FiDollarSign className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-white/80">Payées</p>
              <p className="text-xl font-bold">{stats.paid}</p>
              <p className="text-sm text-white/80">{formatPrice(stats.paidAmount)}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl p-5 text-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/10 rounded-lg">
              <FiCalendar className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-white/80">En attente</p>
              <p className="text-xl font-bold">{stats.pending}</p>
              <p className="text-sm text-white/80">{formatPrice(stats.pendingAmount)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-center">
        <div className="relative flex-1 w-full">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Rechercher par numéro, client..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-invoices"
          />
        </div>
        
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[150px]" data-testid="filter-type">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous types</SelectItem>
            <SelectItem value="deposit">Acompte</SelectItem>
            <SelectItem value="balance">Solde</SelectItem>
            <SelectItem value="full">Totale</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[150px]" data-testid="filter-status">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            <SelectItem value="paid">Payée</SelectItem>
            <SelectItem value="pending">En attente</SelectItem>
            <SelectItem value="cancelled">Annulée</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <FiFileText className="w-5 h-5 text-blue-500 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-800">
              Entreprise non assujettie à la TVA
            </p>
            <p className="text-sm text-blue-600">
              Les factures sont générées automatiquement lors des paiements (acompte et solde).
              Mention "HORS TAXES" affichée sur toutes les factures.
            </p>
          </div>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#d4af37] border-t-transparent" />
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className="text-center py-12">
            <FiFileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {search || filterType !== 'all' || filterStatus !== 'all' 
                ? 'Aucune facture ne correspond à vos critères' 
                : 'Aucune facture pour le moment'}
            </p>
            <p className="text-sm text-gray-400 mt-2">
              Les factures seront générées automatiquement lors des paiements
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">N° Facture</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Montant HT</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredInvoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-gray-50" data-testid={`invoice-row-${invoice.id}`}>
                    <td className="px-4 py-4">
                      <span className="font-mono font-medium text-gray-900">
                        {invoice.invoice_number}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600">
                      {formatDate(invoice.created_at)}
                    </td>
                    <td className="px-4 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{invoice.client_name}</p>
                        <p className="text-sm text-gray-500">{invoice.client_email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      {getInvoiceTypeBadge(invoice.type)}
                    </td>
                    <td className="px-4 py-4">
                      <span className="font-semibold text-gray-900">
                        {formatPrice(invoice.amount)}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      {getStatusBadge(invoice.status)}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSelectedInvoice(invoice);
                            setShowPreview(true);
                          }}
                          title="Aperçu"
                          data-testid={`preview-invoice-${invoice.id}`}
                        >
                          <FiEye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDownloadPDF(invoice.id)}
                          title="Télécharger PDF"
                          data-testid={`download-invoice-${invoice.id}`}
                        >
                          <FiDownload className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleSendEmail(invoice.id)}
                          title="Envoyer par email"
                          data-testid={`send-invoice-${invoice.id}`}
                        >
                          <FiMail className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invoice Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Aperçu de la facture {selectedInvoice?.invoice_number}</DialogTitle>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-6 p-4 bg-white border rounded-lg">
              {/* Invoice Header */}
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">FACTURE</h2>
                  <p className="text-lg font-mono text-[#d4af37]">{selectedInvoice.invoice_number}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-gray-900">Just A Smile Events</p>
                  <p className="text-sm text-gray-600">contact@justasmile-events.com</p>
                </div>
              </div>

              {/* Client Info */}
              <div className="grid grid-cols-2 gap-6 py-4 border-t border-b">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Facturé à :</p>
                  <p className="font-medium">{selectedInvoice.client_name}</p>
                  <p className="text-sm text-gray-600">{selectedInvoice.client_email}</p>
                  {selectedInvoice.client_phone && (
                    <p className="text-sm text-gray-600">{selectedInvoice.client_phone}</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500 mb-1">Date d'émission :</p>
                  <p className="font-medium">{formatDate(selectedInvoice.created_at)}</p>
                  <p className="text-sm text-gray-500 mt-2">Type :</p>
                  <p className="font-medium">{getInvoiceTypeLabel(selectedInvoice.type)}</p>
                </div>
              </div>

              {/* Invoice Items */}
              <div>
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 text-sm text-gray-500">Description</th>
                      <th className="text-right py-2 text-sm text-gray-500">Montant HT</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="py-3">
                        <p className="font-medium">{selectedInvoice.description || 'Prestation événementielle'}</p>
                        {selectedInvoice.reservation_id && (
                          <p className="text-sm text-gray-500">Réf: {selectedInvoice.reservation_id}</p>
                        )}
                      </td>
                      <td className="py-3 text-right font-medium">
                        {formatPrice(selectedInvoice.amount)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Total */}
              <div className="border-t pt-4">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-lg">Total HT</span>
                  <span className="font-bold text-2xl text-[#d4af37]">
                    {formatPrice(selectedInvoice.amount)}
                  </span>
                </div>
                <p className="text-center mt-4 text-sm text-gray-500 bg-gray-100 py-2 rounded">
                  TVA non applicable - Article 293 B du CGI
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  className="flex-1"
                  onClick={() => handleDownloadPDF(selectedInvoice.id)}
                >
                  <FiDownload className="w-4 h-4 mr-2" />
                  Télécharger PDF
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => handleSendEmail(selectedInvoice.id)}
                >
                  <FiMail className="w-4 h-4 mr-2" />
                  Envoyer par email
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
